import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session Control Context
const SessionControlContext = createContext(null);

export const useSessionControl = () => {
  const context = useContext(SessionControlContext);
  if (!context) {
    throw new Error('useSessionControl must be used within SessionControlProvider');
  }
  return context;
};

export const SessionControlProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [fatigueStatus, setFatigueStatus] = useState(null);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [breakRemaining, setBreakRemaining] = useState(0);
  const [sscsEnabled, setSSCSEnabled] = useState(true);
  const [thresholds, setThresholds] = useState({
    suggest_break_minutes: 45,
    enforce_lock_minutes: 60,
    break_duration_minutes: 10
  });
  
  const checkIntervalRef = useRef(null);
  const token = localStorage.getItem('token');

  // Start a new learning session
  const startSession = useCallback(async (activityType, subject = null, topic = null) => {
    if (!token) return null;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/session-control/start`,
        { activity_type: activityType, subject, topic },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSessionId(response.data.session_id);
      setIsActive(true);
      setSSCSEnabled(response.data.sscs_enabled);
      setThresholds(response.data.thresholds);
      setFatigueStatus({ status: 'continue', message: 'Session started!' });
      
      return response.data.session_id;
    } catch (error) {
      console.error('Failed to start session:', error);
      return null;
    }
  }, [token]);

  // Log an answer and check fatigue
  const logAnswer = useCallback(async (questionId, isCorrect, responseTimeSeconds, difficulty = 'medium') => {
    if (!sessionId || !token || !sscsEnabled) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/session-control/log-answer`,
        {
          session_id: sessionId,
          question_id: questionId,
          is_correct: isCorrect,
          response_time_seconds: responseTimeSeconds,
          difficulty
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const status = response.data.fatigue_status;
      setFatigueStatus(status);
      
      // Handle fatigue status
      if (status.status === 'enforce_lock') {
        setShowLockModal(true);
        setShowBreakModal(false);
      } else if (status.status === 'suggest_break' && !showBreakModal) {
        setShowBreakModal(true);
      } else if (status.status === 'on_break') {
        setBreakRemaining(status.break_remaining_minutes || 0);
      }
      
      return status;
    } catch (error) {
      console.error('Failed to log answer:', error);
      return null;
    }
  }, [sessionId, token, sscsEnabled, showBreakModal]);

  // Check current fatigue status
  const checkStatus = useCallback(async () => {
    if (!sessionId || !token || !sscsEnabled) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/session-control/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const status = response.data;
      setFatigueStatus(status);
      
      if (status.status === 'enforce_lock') {
        setShowLockModal(true);
        setShowBreakModal(false);
      } else if (status.status === 'suggest_break' && !showBreakModal && !showLockModal) {
        setShowBreakModal(true);
      } else if (status.status === 'on_break') {
        setBreakRemaining(status.break_remaining_minutes || 0);
      } else if (status.status === 'continue') {
        setShowBreakModal(false);
        setShowLockModal(false);
      }
      
      return status;
    } catch (error) {
      console.error('Failed to check status:', error);
      return null;
    }
  }, [sessionId, token, sscsEnabled, showBreakModal, showLockModal]);

  // Start a break
  const startBreak = useCallback(async () => {
    if (!sessionId || !token) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/session-control/start-break/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowBreakModal(false);
      setShowLockModal(false);
      setBreakRemaining(response.data.break_duration_minutes);
      setFatigueStatus({
        status: 'on_break',
        message: response.data.message,
        allowed_activities: response.data.allowed_activities
      });
      
      toast.success('Break started! Enjoy your rest.', {
        description: `You can review answers or explore the E-Library for ${response.data.break_duration_minutes} minutes.`
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to start break:', error);
      return null;
    }
  }, [sessionId, token]);

  // End break and resume
  const endBreak = useCallback(async () => {
    if (!sessionId || !token) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/session-control/end-break/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBreakRemaining(0);
      setFatigueStatus({ status: 'continue', message: response.data.message });
      setShowBreakModal(false);
      setShowLockModal(false);
      
      toast.success('Welcome back!', {
        description: 'Your session timer has been reset. Keep up the great work!'
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to end break:', error);
      return null;
    }
  }, [sessionId, token]);

  // End session
  const endSession = useCallback(async () => {
    if (!sessionId || !token) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/session-control/end/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSessionId(null);
      setIsActive(false);
      setFatigueStatus(null);
      setShowBreakModal(false);
      setShowLockModal(false);
      
      return response.data.summary;
    } catch (error) {
      console.error('Failed to end session:', error);
      return null;
    }
  }, [sessionId, token]);

  // Dismiss break suggestion (continue anyway)
  const dismissBreakSuggestion = useCallback(() => {
    setShowBreakModal(false);
    toast.info('Continuing session', {
      description: "Remember to take breaks to stay focused!"
    });
  }, []);

  // Check if activity is allowed during break
  const isActivityAllowed = useCallback((activity) => {
    if (!fatigueStatus || fatigueStatus.status === 'continue') return true;
    if (!fatigueStatus.allowed_activities) return false;
    return fatigueStatus.allowed_activities.includes(activity) || 
           fatigueStatus.allowed_activities.includes('all');
  }, [fatigueStatus]);

  // Periodic status check (every 2 minutes)
  useEffect(() => {
    if (isActive && sscsEnabled) {
      checkIntervalRef.current = setInterval(() => {
        checkStatus();
      }, 120000); // 2 minutes
    }
    
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isActive, sscsEnabled, checkStatus]);

  // Break countdown timer
  useEffect(() => {
    let timer;
    if (breakRemaining > 0) {
      timer = setInterval(() => {
        setBreakRemaining(prev => {
          if (prev <= 0.02) { // ~1 second
            checkStatus(); // Check if break is over
            return 0;
          }
          return prev - (1/60); // Decrease by 1 second (in minutes)
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [breakRemaining, checkStatus]);

  const value = {
    // State
    sessionId,
    isActive,
    fatigueStatus,
    showBreakModal,
    showLockModal,
    breakRemaining,
    sscsEnabled,
    thresholds,
    
    // Actions
    startSession,
    logAnswer,
    checkStatus,
    startBreak,
    endBreak,
    endSession,
    dismissBreakSuggestion,
    isActivityAllowed,
    
    // Setters
    setShowBreakModal,
    setShowLockModal
  };

  return (
    <SessionControlContext.Provider value={value}>
      {children}
    </SessionControlContext.Provider>
  );
};

export default SessionControlProvider;
